/**
 * Onglet Aides Complémentaires — Module TEF
 * Détection · Agent IA · Grille des demandes · Workflow de validation
 */
import { useState, useEffect } from "react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { toast } from "sonner";
import {
  RefreshCw, Plus, Trash2, ChevronDown, ChevronUp,
  CheckCircle2, Clock, XCircle, AlertCircle, Euro,
  Building2, Users, MapPin, FileText, Loader2, TrendingDown,
  Info, Send, Bot, ExternalLink, Eye, Mail, Printer, Globe
} from "lucide-react";
import { API, getAuthHeaders, formatDate } from "./helpers";
import { DemandeDetailModal } from "./DemandeDetailModal";
// #359 — bandeau des opportunités, servi par l'Agent Aides Complémentaires.
// Placé EN TÊTE de l'onglet : c'est la première chose qu'un opérateur doit voir.
import { OpportunitesAidesBandeau } from "./OpportunitesAidesBandeau";
// AIDES-04 — étape 1 du parcours : contractualiser AVANT tout recensement.
import { AidesContractualisationPanel } from "./AidesContractualisationPanel";
import { AidesSuiviPanel } from "./AidesSuiviPanel";

// ─── Config statuts aide dossier ─────────────────────────────────────────────

const STATUTS_AIDE = {
  eligible:     { label: "Éligible",      color: "var(--oh-stat-blue-text)", icon: Info },
  non_etudiee:  { label: "Non étudiée",   color: "var(--oh-stat-slate-text)", icon: Clock },
  en_cours:     { label: "En cours",      color: "var(--oh-stat-amber-text)", icon: Clock },
  deposee:      { label: "Déposée",       color: "#8b5cf6", icon: FileText },
  instruction:  { label: "Instruction",   color: "#0891b2", icon: RefreshCw },
  validee:      { label: "Validée",       color: "#10b981", icon: CheckCircle2 },
  refusee:      { label: "Refusée",       color: "var(--oh-stat-red-text)", icon: XCircle },
  payee:        { label: "Payée",         color: "#059669", icon: CheckCircle2 },
};

const TRANSITIONS_AIDE = {
  eligible:    ["non_etudiee", "en_cours"],
  non_etudiee: ["en_cours"],
  en_cours:    ["deposee", "refusee"],
  deposee:     ["instruction", "validee", "refusee"],
  instruction: ["validee", "refusee"],
  validee:     ["payee"],
  refusee:     [],
  payee:       [],
};

// ─── Config statuts demande ───────────────────────────────────────────────────

const STATUTS_DEMANDE = {
  non_etudiee:      { label: "Non étudiée",      color: "var(--oh-stat-slate-text)", bg: "var(--oh-stat-slate-bg)" },
  infos_manquantes: { label: "Infos manquantes",  color: "var(--oh-stat-amber-text)", bg: "#f59e0b10" },
  brouillon_genere: { label: "Brouillon généré",  color: "var(--oh-stat-blue-text)", bg: "#3b82f610" },
  a_valider:        { label: "À valider",          color: "#8b5cf6", bg: "#8b5cf610" },
  validee:          { label: "Validée",            color: "#10b981", bg: "#10b98110" },
  envoyee:          { label: "Envoyée",            color: "#059669", bg: "#05966910" },
  refusee:          { label: "Refusée",            color: "var(--oh-stat-red-text)", bg: "#ef444410" },
  erreur:           { label: "Erreur",             color: "var(--oh-stat-red-text)", bg: "#ef444410" },
};

const CANAL_ICONS = { email: Mail, courrier: Printer, formulaire_web: Globe };
const TYPE_ICONS = {
  caisse_retraite: Users, departement: Building2,
  region: MapPin, ville: MapPin, mutuelle: Building2, autre: Info,
};

function formatMontant(val) {
  if (val === null || val === undefined || val === "") return "—";
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(val);
}

// ─── Badge statut aide ────────────────────────────────────────────────────────

function StatutAideBadge({ statut, aideId, onTransition }) {
  const [open, setOpen] = useState(false);
  const [showMontantInput, setShowMontantInput] = useState(null); // { statut }
  const [montantInput, setMontantInput] = useState("");
  const cfg = STATUTS_AIDE[statut] || STATUTS_AIDE.non_etudiee;
  const Icon = cfg.icon;
  const transitions = TRANSITIONS_AIDE[statut] || [];

  const NEED_MONTANT = ["validee", "payee"]; // Ces statuts demandent un montant

  const handleTransitionClick = (targetStatut) => {
    if (NEED_MONTANT.includes(targetStatut)) {
      setShowMontantInput({ statut: targetStatut });
      setMontantInput("");
      setOpen(false);
    } else {
      onTransition(aideId, targetStatut, null);
      setOpen(false);
    }
  };

  if (transitions.length === 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
        style={{ background: cfg.color + "15", color: cfg.color }}>
        <Icon className="w-3 h-3" />{cfg.label}
      </span>
    );
  }

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium hover:opacity-80"
        style={{ background: cfg.color + "15", color: cfg.color }}
        data-testid={`statut-aide-${aideId}`}>
        <Icon className="w-3 h-3" />{cfg.label}<ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute z-20 top-full left-0 mt-1 rounded-lg shadow-lg py-1 min-w-36"
          style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}>
          {transitions.map(t => {
            const tc = STATUTS_AIDE[t];
            const TI = tc.icon;
            return (
              <button key={t} onClick={() => handleTransitionClick(t)}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:opacity-80"
                style={{ color: tc.color }} data-testid={`transition-${aideId}-${t}`}>
                <TI className="w-3 h-3" />{tc.label}
                {NEED_MONTANT.includes(t) && <span className="ml-auto text-xs opacity-60">+ montant</span>}
              </button>
            );
          })}
        </div>
      )}
      {/* Mini modal saisie montant */}
      {showMontantInput && (
        <div className="absolute z-30 top-full left-0 mt-1 p-3 rounded-lg shadow-xl min-w-52"
          style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}>
          <p className="text-xs font-medium mb-2" style={{ color: "var(--oh-text)" }}>
            Montant {showMontantInput.statut === "validee" ? "accordé" : "versé"} (€)
          </p>
          <input
            type="number"
            value={montantInput}
            onChange={e => setMontantInput(e.target.value)}
            placeholder="Ex: 2500"
            className="w-full px-2 py-1.5 rounded text-xs mb-2"
            style={{ background: "var(--oh-bg)", border: "1px solid var(--oh-border)", color: "var(--oh-text)" }}
            autoFocus
            data-testid={`montant-input-${aideId}`}
          />
          <div className="flex gap-1.5">
            <button
              onClick={() => {
                onTransition(aideId, showMontantInput.statut, montantInput ? parseFloat(montantInput) : null);
                setShowMontantInput(null);
              }}
              className="flex-1 py-1 rounded text-xs font-medium"
              style={{ background: STATUTS_AIDE[showMontantInput.statut]?.color || "#10b981", color: "#fff" }}
              data-testid={`confirmer-montant-${aideId}`}
            >
              Confirmer
            </button>
            <button
              onClick={() => setShowMontantInput(null)}
              className="flex-1 py-1 rounded text-xs"
              style={{ background: "var(--oh-bg)", color: "var(--oh-text-muted)", border: "1px solid var(--oh-border)" }}
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Carte aide ───────────────────────────────────────────────────────────────

function AideCard({ aide, onStatutChange, onRetirer }) {
  const TypeIcon = TYPE_ICONS[aide.aide_type] || Info;

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg"
      style={{ border: "1px solid var(--oh-border)", background: "var(--oh-bg-elevated)" }}
      data-testid={`aide-card-${aide.aide_id}`}>
      <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
        style={{ background: "var(--oh-hub-aides)15" }}>
        <TypeIcon className="w-3.5 h-3.5" style={{ color: "var(--oh-hub-aides)" }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium truncate" style={{ color: "var(--oh-text)" }}>
          {aide.aide_nom}
        </p>
        <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
          {aide.aide_organisme}
          {aide.montant_estime && <span className="ml-2 font-medium" style={{ color: "var(--oh-hub-aides)" }}>est. {formatMontant(aide.montant_estime)}</span>}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <StatutAideBadge statut={aide.statut} aideId={aide.aide_id} onTransition={onStatutChange} />
        <button onClick={() => onRetirer(aide.aide_id)}
          className="p-1 rounded hover:opacity-70" style={{ color: "var(--oh-text-muted)" }}
          data-testid={`retirer-aide-${aide.aide_id}`}>
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

// ─── Ligne grille demande ─────────────────────────────────────────────────────

function DemandeRow({ demande, onOuvrir }) {
  const s = STATUTS_DEMANDE[demande.statut] || STATUTS_DEMANDE.non_etudiee;
  const CanalIcon = CANAL_ICONS[demande.canal] || FileText;
  const score = Math.round((demande.score_eligibilite || 0) * 100);

  return (
    <tr
      className="hover:opacity-90 cursor-pointer transition-opacity"
      onClick={() => onOuvrir(demande)}
      data-testid={`demande-row-${demande.id}`}
    >
      <td className="py-2.5 px-3">
        <p className="text-xs font-medium" style={{ color: "var(--oh-text)" }}>{demande.aide_nom}</p>
        <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>{demande.aide_organisme}</p>
      </td>
      <td className="py-2.5 px-3">
        <span className="text-xs px-1.5 py-0.5 rounded"
          style={{ background: "var(--oh-bg)", color: "var(--oh-text-muted)", border: "1px solid var(--oh-border)" }}>
          {demande.aide_type?.replace("_", " ")}
        </span>
      </td>
      <td className="py-2.5 px-3">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
          style={{ background: s.bg, color: s.color }}>
          {s.label}
        </span>
      </td>
      <td className="py-2.5 px-3">
        <span className="text-xs font-medium" style={{ color: score >= 75 ? "#10b981" : score >= 50 ? "var(--oh-stat-amber-text)" : "var(--oh-stat-red-text)" }}>
          {score}%
        </span>
      </td>
      <td className="py-2.5 px-3">
        <span className="inline-flex items-center gap-1 text-xs" style={{ color: "var(--oh-text-muted)" }}>
          <CanalIcon className="w-3 h-3" />
          {demande.canal}
        </span>
      </td>
      <td className="py-2.5 px-3 text-xs" style={{ color: "var(--oh-text-muted)" }}>
        {formatDate(demande.date_creation)}
      </td>
      <td className="py-2.5 px-3">
        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onOuvrir(demande); }}
          className="text-xs h-7" data-testid={`btn-ouvrir-${demande.id}`}>
          <Eye className="w-3 h-3 mr-1" />
          Ouvrir
        </Button>
      </td>
    </tr>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────

export function AidesComplementairesTab({ dossierId, tenantId, dossier, onUpdate }) {
  const [versionPrestation, setVersionPrestation] = useState(0);
  const [aides, setAides] = useState([]);
  const [demandes, setDemandes] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [financement, setFinancement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);
  const [runningAgent, setRunningAgent] = useState(false);
  const [showCatalogue, setShowCatalogue] = useState(false);
  const [selectedDemande, setSelectedDemande] = useState(null);
  const [activeSection, setActiveSection] = useState("aides"); // "aides" | "demandes"

  const headers = getAuthHeaders(tenantId);

  useEffect(() => {
    loadAll();
  }, [dossierId]);

  async function loadAll() {
    setLoading(true);
    await Promise.all([loadAides(), loadDemandes(), loadFinancement()]);
    setLoading(false);
  }

  async function loadAides() {
    try {
      const res = await fetch(`${API}/api/aides/dossier/${dossierId}`, { headers, credentials: "include" });
      if (res.ok) setAides((await res.json()).aides || []);
    } catch (e) { console.error(e); }
  }

  async function loadDemandes() {
    try {
      const res = await fetch(`${API}/api/aides/dossier/${dossierId}/demandes`, { headers, credentials: "include" });
      if (res.ok) setDemandes((await res.json()).demandes || []);
    } catch (e) { console.error(e); }
  }

  async function loadFinancement() {
    try {
      const res = await fetch(`${API}/api/aides/financement/${dossierId}`, { headers, credentials: "include" });
      if (res.ok) setFinancement(await res.json());
    } catch (e) { /* silent */ }
  }

  async function loadCatalogue() {
    if (catalogue.length > 0) { setShowCatalogue(true); return; }
    try {
      const res = await fetch(`${API}/api/aides/catalogue`, { headers, credentials: "include" });
      if (res.ok) setCatalogue((await res.json()).aides || []);
    } catch (e) { console.error(e); }
    setShowCatalogue(true);
  }

  async function handleDetect() {
    setDetecting(true);
    try {
      const res = await fetch(`${API}/api/aides/detecter/${dossierId}`, {
        method: "POST", headers, credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.aides_eligibles?.length || 0} aide(s) détectée(s)`);
        await loadAides();
        await loadFinancement();
      } else toast.error("Erreur lors de la détection");
    } catch (e) { toast.error("Erreur réseau"); }
    finally { setDetecting(false); }
  }

  async function handleLancerAgent() {
    if (aides.length === 0) {
      toast.error("Lancez d'abord la détection des aides");
      return;
    }
    setRunningAgent(true);
    toast.info(`L'agent analyse ${aides.length} aide(s)… cela peut prendre 1-2 minutes.`, { duration: 8000 });
    try {
      const res = await fetch(`${API}/api/aides/dossier/${dossierId}/agent/analyser`, {
        method: "POST", headers, credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.demandes_creees} demande(s) générée(s) par l'agent`);
        await loadDemandes();
        setActiveSection("demandes");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur agent");
      }
    } catch (e) { toast.error("Erreur réseau"); }
    finally { setRunningAgent(false); }
  }

  async function handleStatutChange(aideId, newStatut, montantAccorde = null) {
    try {
      const body = { statut: newStatut };
      if (montantAccorde !== null) body.montant_accorde = montantAccorde;
      const res = await fetch(`${API}/api/aides/dossier/${dossierId}/aide/${aideId}/statut`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (res.ok) { await loadAides(); await loadFinancement(); }
      else toast.error("Erreur mise à jour");
    } catch (e) { toast.error("Erreur réseau"); }
  }

  async function handleAjouter(aideId) {
    try {
      const res = await fetch(`${API}/api/aides/dossier/${dossierId}/ajouter`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ aide_id: aideId }),
      });
      if (res.ok) {
        toast.success("Aide ajoutée"); setShowCatalogue(false);
        await loadAides(); await loadFinancement();
      } else toast.error((await res.json()).detail || "Erreur ajout");
    } catch (e) { toast.error("Erreur réseau"); }
  }

  async function handleRetirer(aideId) {
    try {
      const res = await fetch(`${API}/api/aides/dossier/${dossierId}/aide/${aideId}`, {
        method: "DELETE", headers, credentials: "include",
      });
      if (res.ok) { await loadAides(); await loadFinancement(); }
    } catch (e) { toast.error("Erreur réseau"); }
  }

  async function handleSupprimerDemande(demandeId) {
    try {
      const res = await fetch(`${API}/api/aides/dossier/${dossierId}/demandes/${demandeId}`, {
        method: "DELETE", headers, credentials: "include",
      });
      if (res.ok) { await loadDemandes(); toast.success("Demande supprimée"); }
    } catch (e) { toast.error("Erreur réseau"); }
  }

  const aidesIds = new Set(aides.map(a => a.aide_id));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--oh-hub-aides)" }} />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="aides-complementaires-tab">

      {/* AIDES-04 — contractualisation : conditions, documents, signature, porte. */}
      <AidesContractualisationPanel dossierId={dossierId} tenantId={tenantId}
        onChange={() => setVersionPrestation((v) => v + 1)} />
      {/* AIDES-06 — recensement → démarches → envois → réponses → honoraires. */}
      <AidesSuiviPanel dossierId={dossierId} tenantId={tenantId} rafraichir={versionPrestation} />

      {/* #359 — « Ce dossier est éligible à N aides non sollicitées. » */}
      <OpportunitesAidesBandeau dossierId={dossierId} tenantId={tenantId} />

      {/* ── Résumé financement ── */}
      {financement && financement.montant_travaux_ttc > 0 && (
        <div className="p-4 rounded-lg space-y-3"
          style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}
          data-testid="financement-summary">
          {/* Ligne principale */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Travaux TTC", val: financement.montant_travaux_ttc, color: "var(--oh-text)" },
              { label: "ANAH (MPA)", val: financement.montant_anah, color: "#10b981" },
              { label: "Aides estimées", val: financement.total_aides_estimees, color: "var(--oh-hub-aides)" },
              { label: "RAC estimé", val: financement.reste_a_charge_estime, color: "var(--oh-stat-amber-text)", icon: TrendingDown },
            ].map(({ label, val, color, icon: Icon }) => (
              <div key={label}>
                <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>{label}</p>
                <div className="flex items-center gap-1 mt-0.5">
                  {Icon && <Icon className="w-3 h-3" style={{ color }} />}
                  <p className="text-sm font-medium" style={{ color }}>{formatMontant(val)}</p>
                </div>
              </div>
            ))}
          </div>
          {/* Ligne RAC réel si des montants ont été accordés */}
          {(financement.total_aides_accordees > 0 || financement.total_aides_versees > 0) && (
            <div className="pt-2 border-t" style={{ borderColor: "var(--oh-border)" }}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>Aides accordées</p>
                  <p className="text-sm font-medium text-[var(--oh-success-text)]">{formatMontant(financement.total_aides_accordees)}</p>
                </div>
                <div>
                  <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>Aides versées</p>
                  <p className="text-sm font-medium text-[var(--oh-success-text)]">{formatMontant(financement.total_aides_versees)}</p>
                </div>
                <div>
                  <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>RAC réel</p>
                  <div className="flex items-center gap-1 mt-0.5">
                    <TrendingDown className="w-3 h-3 text-[var(--oh-info-text)]" />
                    <p className="text-sm font-medium text-[var(--oh-info-text)]">{formatMontant(financement.reste_a_charge_reel)}</p>
                  </div>
                </div>
                <div>
                  <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>Taux couverture</p>
                  <p className="text-sm font-medium"
                    style={{ color: financement.taux_financement_reel >= 70 ? "#10b981" : "var(--oh-stat-amber-text)" }}>
                    {financement.taux_financement_reel?.toFixed(0)}%
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Tabs internes ── */}
      <div className="flex items-center gap-1 p-1 rounded-lg w-fit"
        style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}>
        {[
          { key: "aides", label: `Aides (${aides.length})`, icon: Euro },
          { key: "demandes", label: `Demandes agent (${demandes.length})`, icon: Bot },
        ].map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setActiveSection(key)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all"
            style={{
              background: activeSection === key ? "var(--oh-hub-aides)" : "transparent",
              color: activeSection === key ? "#fff" : "var(--oh-text-muted)",
            }}
            data-testid={`tab-${key}`}>
            <Icon className="w-3 h-3" />{label}
          </button>
        ))}
      </div>

      {/* ════════════════ SECTION AIDES ════════════════ */}
      {activeSection === "aides" && (
        <div className="space-y-3">
          {/* Barre d'actions */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
              Aides détectées dans le dossier
            </p>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={loadCatalogue} className="text-xs h-8"
                data-testid="btn-ajouter-aide">
                <Plus className="w-3 h-3 mr-1" />Ajouter
              </Button>
              <Button size="sm" onClick={handleDetect} disabled={detecting} className="text-xs h-8"
                style={{ background: "var(--oh-hub-aides)", color: "#fff" }}
                data-testid="btn-detecter-aides">
                {detecting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                {detecting ? "Détection…" : "Détecter"}
              </Button>
              <Button size="sm" onClick={handleLancerAgent} disabled={runningAgent || aides.length === 0}
                className="text-xs h-8"
                style={{ background: runningAgent ? "#6b7280" : "#1e293b", color: "#fff" }}
                data-testid="btn-lancer-agent"
                title={aides.length === 0 ? "Détectez d'abord les aides" : "Lancer l'agent IA"}>
                {runningAgent ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Bot className="w-3 h-3 mr-1" />}
                {runningAgent ? "Agent en cours…" : "Lancer l'agent IA"}
              </Button>
            </div>
          </div>

          {/* Catalogue panel */}
          {showCatalogue && (
            <div className="p-4 rounded-lg space-y-2"
              style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-hub-aides)40" }}
              data-testid="catalogue-panel">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium" style={{ color: "var(--oh-text)" }}>Catalogue des aides</p>
                <button onClick={() => setShowCatalogue(false)} className="text-xs" style={{ color: "var(--oh-text-muted)" }}>Fermer</button>
              </div>
              {catalogue.filter(c => !aidesIds.has(c.id)).map(c => (
                <div key={c.id} className="flex items-center justify-between gap-3 p-2 rounded-md"
                  style={{ background: "var(--oh-bg)", border: "1px solid var(--oh-border)" }}>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium" style={{ color: "var(--oh-text)" }}>{c.nom}</p>
                    <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>{c.montant_description}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => handleAjouter(c.id)}
                    className="text-xs h-7 flex-shrink-0" data-testid={`cat-ajouter-${c.id}`}>
                    <Plus className="w-3 h-3 mr-1" />Ajouter
                  </Button>
                </div>
              ))}
              {catalogue.filter(c => !aidesIds.has(c.id)).length === 0 && (
                <p className="text-xs text-center py-2" style={{ color: "var(--oh-text-muted)" }}>
                  Toutes les aides sont déjà dans ce dossier.
                </p>
              )}
            </div>
          )}

          {/* Liste aides */}
          {aides.length === 0 ? (
            <div className="text-center py-10 rounded-lg"
              style={{ border: "1px dashed var(--oh-border)" }} data-testid="empty-aides">
              <Euro className="w-8 h-8 mx-auto mb-3" style={{ color: "var(--oh-text-muted)" }} />
              <p className="text-sm font-medium" style={{ color: "var(--oh-text)" }}>Aucune aide complémentaire</p>
              <p className="text-xs mt-1" style={{ color: "var(--oh-text-muted)" }}>
                Cliquez sur "Détecter" pour analyser automatiquement le DAL.
              </p>
              <Button size="sm" onClick={handleDetect} disabled={detecting}
                className="mt-4 text-xs h-8" style={{ background: "var(--oh-hub-aides)", color: "#fff" }}>
                {detecting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                {detecting ? "Détection…" : "Détecter les aides"}
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {aides.map(aide => (
                <AideCard key={aide.aide_id} aide={aide}
                  onStatutChange={handleStatutChange} onRetirer={handleRetirer} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ════════════════ SECTION DEMANDES ════════════════ */}
      {activeSection === "demandes" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
              Demandes générées par l'agent IA — recherche web · validation · brouillon
            </p>
            <Button size="sm" onClick={handleLancerAgent} disabled={runningAgent || aides.length === 0}
              className="text-xs h-8"
              style={{ background: runningAgent ? "#6b7280" : "#1e293b", color: "#fff" }}
              data-testid="btn-lancer-agent-demandes">
              {runningAgent ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Bot className="w-3 h-3 mr-1" />}
              {runningAgent ? "Agent en cours…" : "Re-lancer l'agent"}
            </Button>
          </div>

          {demandes.length === 0 ? (
            <div className="text-center py-10 rounded-lg"
              style={{ border: "1px dashed var(--oh-border)" }} data-testid="empty-demandes">
              <Bot className="w-8 h-8 mx-auto mb-3" style={{ color: "var(--oh-text-muted)" }} />
              <p className="text-sm font-medium" style={{ color: "var(--oh-text)" }}>Aucune demande générée</p>
              <p className="text-xs mt-1 mb-4" style={{ color: "var(--oh-text-muted)" }}>
                {aides.length === 0
                  ? "Détectez d'abord les aides, puis lancez l'agent."
                  : "Lancez l'agent IA pour rechercher les coordonnées et générer les brouillons."}
              </p>
              {aides.length > 0 && (
                <Button size="sm" onClick={handleLancerAgent} disabled={runningAgent}
                  className="text-xs h-8" style={{ background: "#1e293b", color: "#fff" }}>
                  {runningAgent ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Bot className="w-3 h-3 mr-1" />}
                  {runningAgent ? "Agent en cours…" : "Lancer l'agent IA"}
                </Button>
              )}
            </div>
          ) : (
            <div className="rounded-lg overflow-x-auto"
              style={{ border: "1px solid var(--oh-border)" }}>
              <table className="w-full text-left min-w-[640px]" data-testid="demandes-grid">
                <thead>
                  <tr style={{ background: "var(--oh-bg-elevated)", borderBottom: "1px solid var(--oh-border)" }}>
                    {["Organisme", "Type", "Statut", "Éligibilité", "Canal", "Date", ""].map(h => (
                      <th key={h} className="text-xs font-medium px-3 py-2.5"
                        style={{ color: "var(--oh-text-muted)" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {demandes.map((d, i) => (
                    <tr key={d.id} onClick={() => setSelectedDemande(d)}
                      className="cursor-pointer hover:opacity-90 transition-opacity"
                      style={{ borderBottom: i < demandes.length - 1 ? "1px solid var(--oh-border)" : "none" }}
                      data-testid={`demande-row-${d.id}`}>
                      <td className="py-2.5 px-3">
                        <p className="text-xs font-medium" style={{ color: "var(--oh-text)" }}>{d.aide_nom}</p>
                        <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>{d.aide_organisme}</p>
                      </td>
                      <td className="py-2.5 px-3">
                        <span className="text-xs px-1.5 py-0.5 rounded"
                          style={{ background: "var(--oh-bg)", color: "var(--oh-text-muted)", border: "1px solid var(--oh-border)" }}>
                          {d.aide_type?.replace("_", " ")}
                        </span>
                      </td>
                      <td className="py-2.5 px-3">
                        {(() => {
                          const s = STATUTS_DEMANDE[d.statut] || STATUTS_DEMANDE.non_etudiee;
                          return (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                              style={{ background: s.bg, color: s.color }}>{s.label}</span>
                          );
                        })()}
                      </td>
                      <td className="py-2.5 px-3">
                        <span className="text-xs font-medium" style={{
                          color: (d.score_eligibilite || 0) >= 0.75 ? "#10b981" : (d.score_eligibilite || 0) >= 0.5 ? "var(--oh-stat-amber-text)" : "var(--oh-stat-red-text)"
                        }}>
                          {Math.round((d.score_eligibilite || 0) * 100)}%
                        </span>
                      </td>
                      <td className="py-2.5 px-3">
                        {(() => {
                          const CI = CANAL_ICONS[d.canal] || FileText;
                          return (
                            <span className="inline-flex items-center gap-1 text-xs" style={{ color: "var(--oh-text-muted)" }}>
                              <CI className="w-3 h-3" />{d.canal}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="py-2.5 px-3 text-xs" style={{ color: "var(--oh-text-muted)" }}>
                        {formatDate(d.date_creation)}
                      </td>
                      <td className="py-2.5 px-3">
                        <Button size="sm" variant="outline"
                          onClick={(e) => { e.stopPropagation(); setSelectedDemande(d); }}
                          className="text-xs h-7" data-testid={`btn-ouvrir-${d.id}`}>
                          <Eye className="w-3 h-3 mr-1" />Ouvrir
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Modal détail demande ── */}
      {selectedDemande && (
        <DemandeDetailModal
          demande={selectedDemande}
          dossierId={dossierId}
          tenantId={tenantId}
          onClose={() => setSelectedDemande(null)}
          onRefresh={loadDemandes}
        />
      )}
    </div>
  );
}
